import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { FileText, Type, Tag, ChevronDown, Search, Image } from 'lucide-react';
import { Switch } from '../../components/ui/switch';
import { AIStyleGuide } from './AIStyleGuide';
import { GuideCard } from './GuideCard';
import PromptSelector from './PromptSelector';
import PersistentTextarea from '../../components/ui/PersistentTextArea';
import { useTranslation } from '../../i18n/hooks';

interface AltTextFeatureProps {
  isVisible: boolean;
  isSelected: boolean;
  onVisibilityChange: (visible: boolean) => void;
  onSelectionChange: (selected: boolean) => void;
  errors?: Array<{ field: string; message: string }>;
  prompt: string;
  customPrompt: string;
  onPromptChange: (prompt: string) => void;
  onCustomPromptChange: (customPrompt: string) => void;
}

export const AltTextFeature = ({
  isVisible,
  isSelected,
  onVisibilityChange,
  onSelectionChange,
  errors,
  prompt,               // Use these props instead
  customPrompt,         // of local state
  onPromptChange,
  onCustomPromptChange
  
}: AltTextFeatureProps) => {
  // State
  const { t } = useTranslation();

  const handleHeaderClick = () => {
    if (isSelected) {
      // If already selected, just toggle visibility
      onVisibilityChange(!isVisible);
    } else {
      // If not selected and card is clicked, turn on selection
      // Parent component will handle expanding the card
      onSelectionChange(true);
    }
  };

  const handleToggleChange = (checked: boolean) => {
    onSelectionChange(checked);
  };


  // Compact summary component
  const CompactSummary = () => (
    <div className="mt-2 flex items-center gap-4 text-sm text-gray-600">
      <div className="flex items-center gap-2">
        <Image className="w-4 h-4" />
        <span>
          {prompt === "custom" ? t('enrichmentConfig.custom_prompt') : t('enrichmentConfig.standard_prompt')}
        </span>
      </div>
      <div className="w-1 h-1 bg-gray-300 rounded-full" />
      <div className="flex items-center gap-2">
        <Type className="w-4 h-4" />
        <span>{t('enrichmentConfig.seo_optimized')}</span>
      </div>
    </div>
  );

  return (
    <Card 
      className={`transition-all ${
        isSelected 
          ? "border-blue-200 bg-blue-50/30 shadow-sm ring-1 ring-blue-100" 
          : "hover:bg-gray-50/50"
      }`}
    >
      <CardHeader
        className="cursor-pointer"
        onClick={handleHeaderClick}
      >
        <div className="flex justify-between items-center">
          <div>
            <div className="flex items-center gap-2">
              <CardTitle>{t('enrichmentConfig.feature_alt_text')}</CardTitle>
              {isSelected && (
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                  {t('enrichmentConfig.active')}
                </span>
              )}
            </div>
            <CardDescription>
              {t('enrichmentConfig.customize_alt_text')}
            </CardDescription>
            {!isVisible && isSelected && <CompactSummary />}
          </div>
          <div className="flex items-center gap-4">
          <Switch
              checked={isSelected}
              onCheckedChange={handleToggleChange}
              onClick={(e) => e.stopPropagation()}
            />
            <ChevronDown
              className={`w-5 h-5 text-gray-500 transition-transform ${
                isVisible ? "transform rotate-180" : ""
              }`}
            />
          </div>
        </div>
      </CardHeader>

      {isVisible && (
        <CardContent className="space-y-6">
          <div>
            <PromptSelector
    value={prompt}
    onChange={onPromptChange}

            />
  {prompt === "custom" && (
              <PersistentTextarea
                placeholder={t('enrichmentConfig.enter_custom_prompt')}
                value={customPrompt}
                onChange={onCustomPromptChange}
                className="min-h-[100px] resize-y bg-white mt-4"
                storageKey="alt_text_prompt"
              />
            )}
            <div className="mt-3 space-y-3">
              <div className="text-sm text-gray-500">
                {t('enrichmentConfig.alt_text_example')}
              </div>
            </div>
          </div>

          <GuideCard
            title={t('enrichmentConfig.alt_text_tips')}
            tips={[
              t('enrichmentConfig.alt_text_tip_1'),
              t('enrichmentConfig.alt_text_tip_2'),
              t('enrichmentConfig.alt_text_tip_3'),
              t('enrichmentConfig.alt_text_tip_4')
            ]}
            variant="purple"
          />

          {/* Example section */}
          <Card className="bg-purple-50 border-purple-100">
            <CardContent className="py-3">
              <div className="p-2 bg-purple-100 rounded text-sm text-purple-800">
                <strong>{t('enrichmentConfig.example')}</strong>{" "}
                {t('enrichmentConfig.alt_text_example_value')}
              </div>
            </CardContent>
          </Card>
        </CardContent>
      )}
    </Card>
  );
};